import type { FilterOption } from '@/components/DataTable';
import {
  pluginSourceSchema,
  getPluginSourceLabel,
} from '@kirin/api-client/contracts/plugins';

/** Stable IDs / URL keys for the org Plugins DataTable. */
export const ORG_PLUGIN_TABLE_CONFIG = {
  tableId: 'org-plugins',
} as const;

/**
 * Field IDs used on the Plugins DataTable columns (shared by the org and
 * personal tabs). A column's `id` is also the canonical filter `field` sent to
 * the backend, so the Users column id is `users` (the identity-filter field) —
 * NOT `userCount`, which is the sort field. They are deliberately distinct
 * (KIR-3191 D12). `users` is org-only; the personal tab never renders it.
 */
export const PLUGIN_FIELDS = {
  NAME: 'name',
  SOURCE: 'source',
  MARKETPLACE: 'marketplace',
  USER: 'users',
  FIRST_SEEN_AT: 'firstSeenAt',
  LAST_SEEN_AT: 'lastSeenAt',
} as const;

export type PluginField = (typeof PLUGIN_FIELDS)[keyof typeof PLUGIN_FIELDS];

/** Short URL keys for filter persistence (shared by both scopes). */
export const PLUGIN_FILTER_URL_KEYS: Record<PluginField, string> = {
  [PLUGIN_FIELDS.NAME]: 'pn',
  [PLUGIN_FIELDS.SOURCE]: 'ps',
  [PLUGIN_FIELDS.MARKETPLACE]: 'pm',
  [PLUGIN_FIELDS.USER]: 'pu',
  [PLUGIN_FIELDS.FIRST_SEEN_AT]: 'pfs',
  [PLUGIN_FIELDS.LAST_SEEN_AT]: 'pls',
};

/**
 * Available values for the Source filter — matches `PluginSource` in contracts.
 * Labels are derived from the platform registry via `getPluginSourceLabel`
 * (single source of truth, KIR-3191 D7/RD-11) — no hardcoded label strings, so
 * the dropdown labels and the backend sort labels can never drift.
 *
 * Intentionally NOT gated on `PLATFORM_GEMINI_CLI`: this is a static filter
 * vocabulary, not a rendered platform surface. Before launch the option simply
 * matches zero rows (no `gemini_cli` plugins exist yet), which is harmless;
 * gating it would require threading the feature flag through this module-level
 * constant for no functional gain. The flag still governs the actual Gemini
 * surfaces (registry `released`, posture branch, badges).
 */
export const PLUGIN_SOURCE_OPTIONS: FilterOption[] =
  pluginSourceSchema.options.map(source => ({
    value: source,
    label: getPluginSourceLabel(source),
  }));
